import { SetStateAction, useContext, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { produce } from "immer";

import Select from "shared/components/Form/Select";
import { WizardData } from "shared/components/WizardNew/types";

import { SPLIT_TEST } from "shared/constants/split";
import { DEFAULT_MODE_WIZARD, SAFE_MODE_WIZARD } from "shared/constants/wizard";

import { WizardStore, WizardType } from "shared/context/WizardStoreContext";

import { simplyClick } from "shared/helpers/analytics";
import { isSplitTestRequired } from "shared/helpers/view";
import { setStepperState } from "shared/helpers/wizard";

import { useWizard, WizardStep } from "shared/hooks/useWizard";

import styles from "@platformAssets/components/Wizard.module.scss";

type Range =
  | Record<
      string,
      {
        label?: string | undefined;
        name?: string | undefined;
        answers?: Record<string, any> | undefined;
        reverse?: boolean | undefined;
      }
    >
  | undefined;

type Props = { wizardData: WizardData };

const WizardRange = ({ wizardData }: Props) => {
  const { pathname } = useLocation();
  const wizard = useWizard(wizardData);

  const step = pathname.replace("/", "") as WizardStep;

  const ranges: Range = wizard[step]?.criteria;
  const analytics = wizard[step]?.analytics;

  const [wizardStore, setWizardStore] = useContext(WizardStore);

  const { answers, isNotProvide } = wizardStore;

  const { mode } = wizardData;
  const isSplitReducedWizardEnabled =
    mode === DEFAULT_MODE_WIZARD &&
    isSplitTestRequired(wizard.data, SPLIT_TEST.reducedWizard);

  useEffect(() => {
    if (mode === SAFE_MODE_WIZARD || isSplitReducedWizardEnabled) {
      setWizardStore(
        setStepperState(
          wizardStore,
          isSplitReducedWizardEnabled ? 1 : 2,
          mode,
          isSplitReducedWizardEnabled
        ) as unknown as SetStateAction<WizardType>
      );
    }
  }, [mode, isSplitReducedWizardEnabled]);

  const handleSelect = (option: string, value: number) => {
    const age = answers?.age ?? {};

    if (analytics?.choose) {
      analytics?.choose?.forEach((item: string) => {
        simplyClick(item, null, false);
      });
    }

    const newSelectedOption = {
      ...age,
      [option]: value,
    };

    if (option === "age_from" && age?.age_to < value) {
      newSelectedOption.age_to = value;
    } else if (option === "age_to" && age?.age_from > value) {
      newSelectedOption.age_from = value;
    }

    setWizardStore(
      produce(wizardStore, (wizard: WizardType) => {
        wizard.answers[step] = newSelectedOption;
      })
    );
  };

  if (!ranges) return null;

  return (
    <div className={styles.wizardRangesContainer}>
      {Object.entries(ranges).map(([range, { label, answers, reverse }]) => (
        <div className={styles.wizardRangeWrapper} key={range}>
          <div className={styles.wizardRangeLabel}>{label}</div>

          <Select
            // TODO: REMOVE AFTER SELECT REFACTOR
            className={styles.wizardRangeSelect}
            values={answers}
            reverse={reverse}
            // TODO: REMOVE AFTER SELECT REFACTOR
            value={wizardStore?.answers?.[step]?.[range] ?? ""}
            disabled={isNotProvide}
            handleChange={(value: string) => handleSelect(range, +value)}
            name={range}
          />
        </div>
      ))}
    </div>
  );
};

export { WizardRange };
